import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Target,
  ClipboardCheck,
  GitBranch,
  Search,
  Users,
  Building2,
  BarChart3,
  Compass,
  Landmark,
  GraduationCap,
  LibraryBig,
  BookOpen,
  ChevronDown,
  Play,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentUser, isAdmin, isSupervisorOrAbove } from "@/hooks/use-current-user";

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  supervisorOnly?: boolean;
}

const mainNavItems: NavItem[] = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { label: "Team Dashboard", href: "/dashboard/team", icon: BarChart3 },
  { label: "My Objectives", href: "/objectives", icon: Target },
  { label: "Team Objectives", href: "/objectives/team", icon: Users, supervisorOnly: true },
  { label: "Reviews", href: "/reviews", icon: ClipboardCheck },
  { label: "Alignment", href: "/alignment", icon: GitBranch },
  { label: "Search", href: "/search", icon: Search },
];

const adminNavItems: NavItem[] = [
  { label: "Users", href: "/admin/users", icon: Users },
  { label: "Teams", href: "/admin/teams", icon: Building2 },
  { label: "ITS Strategic Focus", href: "/admin/strategic-plans", icon: Compass },
  { label: "College Strategy", href: "/admin/college-strategy", icon: Landmark },
  { label: "Coach Prompts", href: "/admin/coach-prompts", icon: GraduationCap },
  { label: "OKR Examples", href: "/admin/okr-examples", icon: LibraryBig },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { currentUser } = useCurrentUser();
  const showAdmin = isAdmin(currentUser?.role);
  const showSupervisor = isSupervisorOrAbove(currentUser?.role);
  const [learningOpen, setLearningOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
        />
      )}

      <aside
        aria-label="Sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 max-w-[80vw] flex-col bg-[hsl(var(--sidebar-background))] text-[hsl(var(--sidebar-foreground))] transition-transform duration-300 lg:sticky lg:top-[var(--env-banner-h)] lg:h-[calc(100vh-var(--env-banner-h))] lg:max-w-none lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center border-b border-[hsl(var(--sidebar-border))] px-6">
          <img
            src="/hillsdale-college-logo-white.svg"
            alt="Hillsdale College"
            className="h-8 w-auto"
          />
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3" aria-label="Main navigation">
            {mainNavItems
              .filter((item) => !item.supervisorOnly || showSupervisor)
              .map((item) => (
                <NavLink
                  key={item.href}
                  item={item}
                  isActive={currentPath === item.href}
                  onClick={onClose}
                />
              ))}
          </nav>

          <Separator className="mx-3 my-4 bg-[hsl(var(--sidebar-border))]" />

          <div className="px-3">
            <button
              type="button"
              onClick={() => setLearningOpen((o) => !o)}
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-[hsl(var(--sidebar-foreground))]/80 transition-colors hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]"
            >
              <span className="flex items-center gap-3">
                <BookOpen className="h-4 w-4 shrink-0" />
                OKR Learning
              </span>
              <ChevronDown
                className={cn("h-3 w-3 transition-transform duration-200", learningOpen && "rotate-180")}
              />
            </button>
            {learningOpen && (
              <div className="mt-1 space-y-1 pl-10">
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs text-[hsl(var(--sidebar-foreground))]/70 transition-colors hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]"
                >
                  <Play className="h-3 w-3 shrink-0" />
                  What&rsquo;s a Good OKR? (5 min)
                </button>
                <a
                  href="/WhatMatters.com_-_Google_s_OKR_Playbook.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-md px-3 py-1.5 text-xs text-[hsl(var(--sidebar-foreground))]/70 transition-colors hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]"
                >
                  <FileText className="h-3 w-3 shrink-0" />
                  Google&rsquo;s OKR Playbook (PDF)
                </a>
              </div>
            )}
          </div>

          {showAdmin && (
            <>
              <Separator className="mx-3 my-4 bg-[hsl(var(--sidebar-border))]" />

              <div className="px-3">
                <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-foreground))]/60">
                  Admin
                </p>
                <nav className="space-y-1" aria-label="Admin navigation">
                  {adminNavItems.map((item) => (
                    <NavLink
                      key={item.href}
                      item={item}
                      isActive={currentPath === item.href}
                      onClick={onClose}
                    />
                  ))}
                </nav>
              </div>
            </>
          )}
        </ScrollArea>
      </aside>
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-3xl overflow-hidden p-0">
          <DialogHeader className="px-6 pb-4 pt-6">
            <DialogTitle>What&rsquo;s a Good OKR?</DialogTitle>
          </DialogHeader>
          {videoOpen && (
            <div className="relative pb-[56.25%]">
              <iframe
                src="https://www.youtube.com/embed/JljIxW5Ad4E?autoplay=1"
                title="What's a Good OKR?"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function NavLink({
  item,
  isActive,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      search={(prev) => prev}
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
        isActive
          ? "bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-accent-foreground))]"
          : "text-[hsl(var(--sidebar-foreground))]/80 hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {item.label}
    </Link>
  );
}
